import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BillingOverviewDto } from '@eskoolhub/types';
import { PageHeader } from '../components/AppShell';
import { useToast } from '../components/Toasts';
import { ErrorState, InvoiceStatusChip, SkeletonRows, SkeletonTiles, StatTile } from '../components/ui';
import { api, ApiRequestError } from '../lib/api';
import { formatDateShort, formatINR, formatINRCompact, formatNumber, formatRate, pluralise } from '../lib/format';

export function BillingPage() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['billing'],
    queryFn: () => api.get<BillingOverviewDto>('/api/billing/overview'),
  });

  const markPaid = useMutation({
    mutationFn: (invoiceId: string) => api.post(`/api/billing/invoices/${invoiceId}/mark-paid`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries();
      toast.push('Invoice marked paid', 'success');
    },
    onError: (err) => toast.push(err instanceof ApiRequestError ? err.message : 'Could not update the invoice', 'error'),
  });

  const growth = data?.byPlan.find((p) => p.key === 'GROWTH');

  return (
    <>
      <PageHeader
        title="Billing & plans"
        meta={
          data
            ? `${formatINRCompact(data.mrrPaise)} MRR · ${new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}`
            : 'Loading…'
        }
        actions={
          <button
            className="btn btn--ghost"
            onClick={() => toast.push('Invoice export lands with the PDF/R2 pipeline', 'info')}
          >
            Export invoices
          </button>
        }
      />

      <div className="page">
        {error ? (
          <ErrorState
            message={error instanceof ApiRequestError ? error.message : 'Could not load billing'}
            onRetry={() => void refetch()}
          />
        ) : isPending ? (
          <>
            <SkeletonTiles />
            <SkeletonRows />
          </>
        ) : (
          <>
            <div className="grid-4">
              <StatTile label="MRR" value={formatINR(data.mrrPaise)} caption={data.mrrChangeBps !== null ? `${data.mrrChangeBps > 0 ? '+' : ''}${(data.mrrChangeBps / 100).toFixed(1)}% vs last month` : 'first billing month'} dark />
              <StatTile
                label="Growth plan"
                value={formatNumber(growth?.schoolCount ?? 0)}
                caption={`${formatINR(growth?.mrrPaise ?? 0)} MRR`}
              />
              <StatTile
                label="Trials"
                value={formatNumber(data.trialCount)}
                caption={
                  data.trialNextExpiryDays !== null
                    ? `Converts or ends in ${pluralise(data.trialNextExpiryDays, 'day')}`
                    : 'none running'
                }
                tone={data.trialCount > 0 ? 'warn' : undefined}
              />
              <StatTile
                label="Overdue invoices"
                value={formatNumber(data.overdueCount)}
                caption={data.overdueSchoolNames.join(', ') || 'all clear'}
                tone={data.overdueCount > 0 ? 'danger' : undefined}
              />
            </div>

            <div className="card card--flush">
              <table className="table">
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>School</th>
                    <th style={{ width: 140, textAlign: 'right' }}>Amount</th>
                    <th style={{ width: 120 }}>Due</th>
                    <th style={{ width: 140 }}>Status</th>
                    <th style={{ width: 120 }} />
                  </tr>
                </thead>
                <tbody>
                  {data.invoices.map((invoice) => (
                    <tr key={invoice.id} style={{ cursor: 'default' }}>
                      <td className="mono" style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
                        {invoice.number}
                      </td>
                      <td style={{ fontSize: 14, fontWeight: 700 }}>{invoice.schoolName}</td>
                      <td className="num" style={{ fontWeight: 600 }}>
                        {formatINR(invoice.amountPaise)}
                      </td>
                      <td className="mono" style={{ fontSize: 13, color: invoice.status === 'OVERDUE' ? 'var(--coral)' : 'var(--ink-soft)' }}>
                        {formatDateShort(invoice.dueAt)}
                      </td>
                      <td>
                        <InvoiceStatusChip status={invoice.status} overdueDays={invoice.overdueDays} />
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {invoice.status === 'OVERDUE' || invoice.status === 'SENT' ? (
                          <button
                            className="btn btn--soft btn--sm"
                            onClick={() => markPaid.mutate(invoice.id)}
                            disabled={markPaid.isPending}
                          >
                            Mark paid
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="card">
              <div className="card__title">Plans</div>
              <div className="grid-3">
                {data.plans.map((plan) => (
                  <div
                    key={plan.key}
                    style={{
                      border: plan.key === 'GROWTH' ? '1.5px solid var(--marigold)' : '1px solid var(--rule)',
                      background: plan.key === 'GROWTH' ? 'rgba(255,174,26,.06)' : undefined,
                      borderRadius: 16,
                      padding: 16,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                    }}
                  >
                    <div style={{ fontSize: 15, fontWeight: 700 }}>{plan.name}</div>
                    <div className="mono" style={{ fontSize: 22, fontWeight: 600 }}>
                      {plan.pricePerStudentPerMonthPaise ? (
                        <>
                          {formatRate(plan.pricePerStudentPerMonthPaise)}
                          <span style={{ fontSize: 13, color: 'var(--ink-soft)' }}>/student/mo</span>
                        </>
                      ) : (
                        'Custom'
                      )}
                    </div>
                    <div className="caption">
                      {plan.pricePerStudentPerYearPaise
                        ? `${formatRate(plan.pricePerStudentPerYearPaise)}/student/yr · `
                        : 'Rate set per school · '}
                      {plan.tagline}
                    </div>
                    <div className="caption" style={{ marginTop: 'auto', paddingTop: 8, borderTop: '1px solid var(--rule)' }}>
                      {pluralise(plan.schoolCount, 'school')} · {plan.defaultModules.length} modules ·{' '}
                      {formatINRCompact(plan.mrrPaise)} MRR
                      {plan.customPricedCount > 0 ? ` · ${plan.customPricedCount} negotiated` : ''}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
